import { useReadOnly } from '../../Hooks/useReadOnly';
import { Permissions } from '../../Security/permissions';
import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col } from 'react-bootstrap';
import Table from '../../Components/Table/Table';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { SchedulerDataService } from '../../Services/SchedulerDataService';
import { formatGuatemalanDateTime } from '../../Utils/dateFormat';
// import SchedulerTaskDetailsModal from './SchedulerTaskDetailsModal';
import TaskSchedulerModal from './TaskSchedulerModal';
import TaskConfigModal    from './TaskConfigModal';   // NEW
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faGlasses, faPlay, faTrash } from '@fortawesome/free-solid-svg-icons';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import './SchedulerTaskListPage.scss';
import cronstrue from 'cronstrue';

const SchedulerTaskListPageContent = () => {
    const readOnly = useReadOnly(Permissions.Extractions.ScheduledTasks);
    const { instance, accounts, inProgress } = useMsal();
    const [tasks, setTasks] = useState([]);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showConfigModal,   setShowConfigModal]   = useState(false);
    const [showScheduleModal, setShowScheduleModal] = useState(false);
    const [pendingMeta,       setPendingMeta]       = useState(null); // data from step-1

    // Fetch scheduler tasks once the user is authenticated.
    useEffect(() => {
        if (inProgress === "none" && accounts.length > 0) {
            fetchScheduledTasks();
        }
    }, [inProgress, accounts, instance]);

    const fetchScheduledTasks = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            const data = await SchedulerDataService.getScheduledJobs(response.accessToken);
            setTasks(data);
        } catch (error) {
            console.error("Failed to fetch scheduled tasks:", error);
        }
    };

    const handleSelectTask = async (jobId) => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            // Assuming SchedulerDataService provides a method to get task details.
            const data = await SchedulerDataService.getScheduledJobDetails(jobId, response.accessToken);
            setSelectedTask(data);
            setShowDetailsModal(true);
        } catch (error) {
            console.error("Failed to fetch scheduled task details:", error);
        }
    };

    const handleTriggerTask = async (jobId) => {
        if (readOnly) return;
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            await SchedulerDataService.triggerScheduledJob(jobId, response.accessToken);
            // Optionally refresh the list after triggering.
            fetchScheduledTasks();
        } catch (error) {
            console.error("Failed to trigger scheduled task:", error);
        }
    };

    const handleDeleteTask = async (jobId) => {
        if (readOnly) return;
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            await SchedulerDataService.deleteScheduledJob(jobId, response.accessToken);
            fetchScheduledTasks();
        } catch (error) {
            console.error("Failed to delete scheduled task:", error);
        }
    };

    const handleSaveScheduledTask = async (jobData) => {
        if (readOnly) return;
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            await SchedulerDataService.createScheduledJob(jobData, response.accessToken);
            setShowScheduleModal(false);
            fetchScheduledTasks();
        } catch (error) {
            console.error("Failed to create scheduled task:", error);
        }
    };

    // Map task status to Bootstrap badge classes.
    const getStatusBadgeClass = (status) => {
        if (status === 'Active') return 'text-bg-success';
        else if (status === 'Paused') return 'text-bg-warning';
        else if (status === 'Error') return 'text-bg-danger';
        else return 'text-bg-secondary';
    };

    // Define columns for the scheduler tasks table.
    const columns = React.useMemo(() => [
        {
            header: 'ID',
            accessorKey: 'jobId'
        },
        {
            header: 'Task Type',
            accessorKey: 'taskType'
        },
        {
            header: 'Task Name',
            accessorKey: 'taskName'
        },
        {
            header: 'Recurrence',
            accessorKey: 'cronExpression',
            cell: info => {
              const expr = info.getValue();
              try {
                // this will produce a nice English description
                return cronstrue.toString(expr, { 
                  // optionally: locale: 'es' for Spanish, etc.
                  throwExceptionOnParseError: false 
                });
              } catch {
                // fallback to raw if something goes wrong
                return expr;
              } 
            }
          },
        {
            header: 'Status',
            accessorKey: 'status',
            cell: info => <span className={`badge ${getStatusBadgeClass(info.getValue())}`}>{info.getValue()}</span>
        },
        {
            header: 'Created',
            accessorKey: 'createdDate',
            cell: info => formatGuatemalanDateTime(info.getValue())
        },
        {
            header: 'Actions',
            id: 'actions',
            cell: ({ row }) => (
                <div className="d-flex justify-content-around">
                    {/* <Button size="sm" variant="outline-dark" onClick={() => handleSelectTask(row.original.jobId)}>
                        <FontAwesomeIcon icon={faGlasses} />
                    </Button> */}
                    {/* <Button size="sm" variant="outline-success" onClick={() => handleTriggerTask(row.original.jobId)}>
                        <FontAwesomeIcon icon={faPlay} />
                    </Button> */}
                    <Button disabled={readOnly} size="sm" variant="outline-danger" onClick={() => handleDeleteTask(row.original.jobId)}>
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </div>
            )
        }
    ], [readOnly]);

    const memoizedData = React.useMemo(() => tasks, [tasks]);

    return (
        <Container fluid className="h-100 d-flex flex-column SchedulerTaskListPage">
            <div className='d-flex justify-content-end w-100'>
                    <button className='btn btn-xl btn-outline-primary fw-bold me-2' onClick={fetchScheduledTasks}>
                      <FontAwesomeIcon icon={faRefresh} />
                    </button>
                    {/* <button className='btn btn-xl btn-primary fw-bold' onClick={handleNavigateToNewTask}>New Task</button> */}
                <button disabled={readOnly}
                    className='btn btn-xl btn-primary fw-bold'
                    onClick={() => setShowConfigModal(true)}
                >
                    New Task
                </button>
                  </div>
            <Row className="my-3">
                <Col sm={12}>
                    <Table 
                        columns={columns}
                        data={memoizedData}
                        tableClass="table-hover"
                        containerClass="table-responsive"
                        headerClass="table-light"
                    />
                </Col>
            </Row>
            {/* {showDetailsModal && (
                <SchedulerTaskDetailsModal 
                    show={showDetailsModal}
                    onHide={() => setShowDetailsModal(false)}
                    task={selectedTask}
                    onRefresh={fetchScheduledTasks}
                />
            )} */}
            <TaskSchedulerModal
                show={showScheduleModal}
                onHide={() => setShowScheduleModal(false)}
                onSave={(recurrence) => {
                    // recurrence = { cronExpression, status }
                    const job = {
                        ...recurrence,
                        ...pendingMeta,               // taskType + parameters blob
                        taskName: pendingMeta.taskType, // or let user edit later
                        createdBy: accounts[0].username
                    };
                    handleSaveScheduledTask(job);
                }}
            />

            <TaskConfigModal
                show={showConfigModal}
                onHide={() => setShowConfigModal(false)}
                onNext={(meta) => {
                    setPendingMeta(meta);        // { taskType, parameters }
                    setShowConfigModal(false);
                    setShowScheduleModal(true);  // open step-2
                }}
            />
        </Container>
    );
};

const SchedulerTaskListPage = () => {
    return (
        <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
            <PageLayout2 title="Scheduler Tasks">
                <SchedulerTaskListPageContent />
            </PageLayout2>
        </MsalAuthenticationTemplate>
    );
};

export default SchedulerTaskListPage;
